import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';

import { CreateLabelParams, LabelResult, createLabel } from 'stores/LabelStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

type Props = {
  /** 選択候補のラベル一覧（論理削除されていない有効なもの） */
  labels: LabelResult[];
  /** 選択中の labelId リスト */
  selectedLabelIds: string[];
  /** 選択変更ハンドラ */
  onChange: (labelIds: string[]) => void;
  /** ラベルを新規作成したときに候補一覧を更新するためのコールバック */
  onLabelCreated: (label: LabelResult) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
};

/** Autocomplete の「新規作成」用の特殊オプション型 */
type LabelOption = LabelResult & { inputValue?: string };

const filter = createFilterOptions<LabelOption>();

const DEFAULT_NEW_COLOR = '#1976d2';

/**
 * ラベルのマルチセレクト。
 * - 既存ラベル（API6207 取得）から複数選択できる。
 * - 入力欄に無い名前を打つと「"XXX" を新規作成」候補が出て、押下でカラーピッカー付きダイアログを開き作成（API6208）。
 *   作成したラベルはその場で選択状態になる。
 */
const LabelMultiSelect: React.FC<Props> = ({
  labels,
  selectedLabelIds,
  onChange,
  onLabelCreated,
  label = 'ラベル',
  error = false,
  helperText,
  disabled = false,
  required = false,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newColor, setNewColor] = React.useState(DEFAULT_NEW_COLOR);
  const [creating, setCreating] = React.useState(false);

  const selectedLabels = labels.filter((l) => selectedLabelIds.includes(l.labelId));

  const openCreateDialog = (presetName: string) => {
    setNewName(presetName);
    setNewColor(DEFAULT_NEW_COLOR);
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      errorDefault('ラベル名を入力してください');
      return;
    }
    setCreating(true);
    try {
      const params: CreateLabelParams = { name, color: newColor };
      const labelId = await createLabel(params);
      const created: LabelResult = { labelId, name, color: newColor };
      onLabelCreated(created);
      onChange([...selectedLabelIds, labelId]);
      successDefault('ラベルを作成しました');
      setDialogOpen(false);
    } catch (e) {
      errorDefault('ラベルの作成に失敗しました', toErrorDetail(e));
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Autocomplete<LabelOption, true, false, false>
        multiple
        options={labels as LabelOption[]}
        value={selectedLabels as LabelOption[]}
        disabled={disabled}
        disableCloseOnSelect
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.labelId === value.labelId}
        onChange={(_, newValue) => {
          // 「新規作成」オプション（inputValue 付き）が選ばれたらダイアログを開く
          const createOption = newValue.find((v) => v.inputValue !== undefined);
          if (createOption) {
            openCreateDialog(createOption.inputValue ?? '');
            return;
          }
          onChange(newValue.map((v) => v.labelId));
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const input = params.inputValue.trim();
          const exists = options.some((o) => o.name.toLowerCase() === input.toLowerCase());
          if (input !== '' && !exists) {
            filtered.push({
              labelId: `__create__${input}`,
              name: `「${input}」を新規作成`,
              color: DEFAULT_NEW_COLOR,
              inputValue: input,
            });
          }
          return filtered;
        }}
        renderOption={(optionProps, option) => {
          const { key, ...rest } = optionProps as React.HTMLAttributes<HTMLLIElement> & {
            key: React.Key;
          };
          if (option.inputValue !== undefined) {
            return (
              <Box
                component="li"
                key={String(key)}
                {...rest}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" fontWeight={600}>
                  {option.name}
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              component="li"
              key={String(key)}
              {...rest}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: option.color,
                  flexShrink: 0,
                  border: 1,
                  borderColor: 'divider',
                }}
              />
              <Typography variant="body2">{option.name}</Typography>
            </Box>
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...tagProps}
                label={option.name}
                size="small"
                sx={{
                  borderRadius: 1.5,
                  fontWeight: 500,
                  color: '#fff',
                  bgcolor: option.color,
                  '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)' },
                }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            required={required}
            error={error}
            helperText={helperText}
            placeholder={selectedLabels.length === 0 ? 'ラベルを選択 / 新規作成...' : ''}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        )}
        noOptionsText="該当するラベルがありません"
        sx={{ width: '100%' }}
      />

      {/* ラベル新規作成ダイアログ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            ラベルを新規作成
          </Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="ラベル名"
              size="small"
              fullWidth
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              inputProps={{ maxLength: 64 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                色
              </Typography>
              <Box
                component="input"
                type="color"
                value={newColor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewColor(e.target.value)}
                sx={{
                  width: 48,
                  height: 32,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 0,
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                }}
              />
              <Chip
                label={newName.trim() || 'プレビュー'}
                size="small"
                sx={{ borderRadius: 1.5, color: '#fff', bgcolor: newColor, fontWeight: 500 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || newName.trim() === ''}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LabelMultiSelect;
